import reportEmpty from '@assets/img/svg/report-empty.svg'
import { withTransaction } from '@elastic/apm-rum-react'
import { Campaign, CampaignStatisticsUser, LogService, MariaHillService, UserInDetails } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import EmptyPageComponent from '@src/components/empty-page/empty-page.component'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import ContentMetricTypeComponent from './components/content-metric-type/content-metric-type.component'
import HeaderTextCampaignFormat from './components/header-text-campaign-format/header-text-campaign-format.component'
import SectionShareLinksComponent from './components/section-share-link/section-share-link.component'
import './report.page.scoped.scss'

interface ReportProps extends React.HTMLAttributes<HTMLDivElement> {
  campaign: Campaign
  userInCampaign?: UserInDetails
}

const ReportPageComponent = ({ campaign, userInCampaign }: ReportProps) => {
  const [t] = useTranslation('reportPage')
  const logService = useMemo(() => new LogService(), [])
  const mariaHillService = useMemo(() => new MariaHillService(), [])
  const [statisticsUser, setStatisticsUser] = useState<CampaignStatisticsUser>(null)
  const [loadingStatistics, setLoadingStatistics] = useState(false)
  const [emptyStatistics, setEmptyStatistics] = useState(true)
  const navigate = useNavigate()
  logService.init('Page report Component')

  const metricsList = useMemo(
    () => [
      {
        value: 'impressions',
        percentage: false,
        mediaTypes: ['posts', 'stories', 'reels'],
        mainMetric: ['awareness', 'conversion'].includes(campaign?.planning?.campaignFormat),
      },
      {
        value: 'reach',
        percentage: false,
        mediaTypes: ['posts', 'stories', 'reels'],
        mainMetric: campaign?.planning?.campaignFormat === 'awareness',
      },
      {
        value: 'effectiveReach',
        percentage: true,
        mediaTypes: ['posts', 'stories', 'reels'],
        mainMetric: campaign?.planning?.campaignFormat === 'awareness',
      },
      {
        value: 'engagementRate',
        percentage: true,
        mediaTypes: ['posts', 'stories', 'reels'],
        mainMetric: campaign?.planning?.campaignFormat === 'branding',
      },
      {
        value: 'engagement',
        percentage: false,
        mediaTypes: ['posts', 'stories', 'reels'],
        mainMetric: campaign?.planning?.campaignFormat === 'branding',
      },
    ],
    [campaign]
  )

  const onGetStatisticsUser = useCallback(() => {
    if (!loadingStatistics) {
      setLoadingStatistics(true)

      mariaHillService
        .getStatistics(campaign._id, userInCampaign?.idNetwork)
        .then((response) => {
          setStatisticsUser(response)
          metricsList?.map((item) => {
            if (response?.[item?.value].posts?.position?.total || response?.[item?.value].stories?.position?.total) {
              setEmptyStatistics(false)
            }
          })
          setLoadingStatistics(false)
        })
        .catch((error) => {
          setLoadingStatistics(false)
          logService.error({ message: 'Failed to get statistics report', error })
        })
    }
  }, [campaign, userInCampaign, loadingStatistics, metricsList, mariaHillService, logService])

  const redirect = () => {
    navigate(`/campaigns/${campaign?._id}/details/${userInCampaign?.idNetwork}/orientation`)
  }

  useEffect(() => {
    if (campaign?.socialNetworks[0] === 'instagram') {
      onGetStatisticsUser()
    } else {
      redirect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loadingStatistics) {
    return (
      <div className="display-flex justify-content-center">
        <SqLoader size="big" />
      </div>
    )
  }

  if (emptyStatistics) {
    return (
      <div className="engage-ranking-page">
        <EmptyPageComponent
          title={t('emptyReport')}
          description={t('emptyDescriptionReport')}
          image={reportEmpty}
          icon={'fa-regular fa-chart-line'}
        />
      </div>
    )
  }
  return (
    <div>
      <HeaderTextCampaignFormat campaignFormat={campaign?.planning?.campaignFormat} date={statisticsUser?.date} />
      <hr />
      <div>
        {metricsList?.map((item, index) => (
          <ContentMetricTypeComponent
            key={`${index}-${item?.value}`}
            campaign={campaign}
            metricType={item?.value}
            metric={statisticsUser?.[item?.value]}
            mainMetric={item?.mainMetric}
            mediaTypes={item?.mediaTypes}
            percent={item?.percentage}
            socialId={userInCampaign?.idNetwork}
          />
        ))}
        <SectionShareLinksComponent campaign={campaign} statisticsUser={statisticsUser} />
      </div>
    </div>
  )
}

export default withTransaction('ReportPageComponent', 'component')(ReportPageComponent)
